import { useEffect, useRef } from "react";
import type { InboxMessagesView } from "./use-inbox.js";
import {
  messagesHavePendingStorageAttachments,
  nextPendingAttachmentPollDelayMs,
  shouldContinuePendingAttachmentPolling,
} from "./pending-attachment-reconciliation.js";

export function usePendingAttachmentReconciliation({
  selectedConversationId,
  messagesView,
  refreshMessages,
  messagesFetchInFlightRef,
}: {
  selectedConversationId: number | null;
  messagesView: InboxMessagesView;
  refreshMessages: () => void;
  messagesFetchInFlightRef: React.MutableRefObject<boolean>;
}): void {
  const attemptRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    attemptRef.current = 0;
  }, [selectedConversationId]);

  useEffect(() => {
    const clearTimer = (): void => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    clearTimer();

    if (selectedConversationId == null) {
      return clearTimer;
    }
    if (messagesView.kind !== "ready") {
      return clearTimer;
    }
    if (document.visibilityState !== "visible") {
      return clearTimer;
    }

    const hasPending = messagesHavePendingStorageAttachments(messagesView.items);
    if (!hasPending) {
      attemptRef.current = 0;
      return clearTimer;
    }

    const scheduleNext = (): void => {
      if (!shouldContinuePendingAttachmentPolling(attemptRef.current, true)) {
        return;
      }
      const delay = nextPendingAttachmentPollDelayMs(attemptRef.current);
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        if (selectedConversationId == null) {
          return;
        }
        if (document.visibilityState !== "visible") {
          return;
        }
        if (messagesFetchInFlightRef.current) {
          scheduleNext();
          return;
        }
        attemptRef.current += 1;
        refreshMessages();
      }, delay);
    };

    scheduleNext();
    return clearTimer;
  }, [messagesView, messagesFetchInFlightRef, refreshMessages, selectedConversationId]);
}
